import { ColumnDef, ExpandedState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataNode } from '@/types';
import { DecimalNumber } from './DecimalNumber';
import { NodeIcon } from './NodeIcon';
import { RowExpander } from './RowExpander';
import { CumulativeVariancesHeader } from './CumulativeVariancesHeader';
import { getCellStyle, getRowStyle } from './ReconciliationTable.utils';

interface ColumnBuilderProps {
  expanded: ExpandedState;
  isExpanding: boolean;
  handleRowExpansion: (rowId: string, hasChildren: boolean) => void;
  viewTransaction: (row: DataNode) => void;
  calculatedCumilativeFromDate: string | null | undefined;
  businessDay: string | undefined;
  cumulativeFromDate: string | null | undefined;
  getCumulativeTooltipText: (
    fromDate: string | null | undefined,
    toDate: string | undefined,
    fallbackDate: string | null | undefined
  ) => string;
}

/**
 * Create table columns for ReconciliationTable
 * Handles rendering of hierarchy, verification data, and actions
 */
export function createTableColumns({
  expanded,
  isExpanding,
  handleRowExpansion,
  viewTransaction,
  calculatedCumilativeFromDate,
  businessDay,
  cumulativeFromDate,
  getCumulativeTooltipText,
}: ColumnBuilderProps): ColumnDef<DataNode>[] {
  return [
    {
      id: 'topic',
      header: 'Hierarchy',
      accessorKey: 'NodeLabel',
      cell: ({ row, getValue }) => {
        const depth = row.depth;
        const hasChildren =
          row.original.ChildNodes && row.original.ChildNodes.length > 0;
        const rowId = row.original.Id;
        const isExpanded = (expanded as Record<string, boolean>)[rowId] === true;
        const value = getValue() as string;
        const nodeType = row.original.NodeTag;

        return (
          <div
            className={`flex items-center space-x-2 ${hasChildren ? 'cursor-pointer' : ''}`}
            style={{ paddingLeft: `${depth * 8}px` }}
            onClick={() => handleRowExpansion(rowId, !!hasChildren)}
          >
            <RowExpander
              isExpanded={isExpanded}
              isExpanding={isExpanding}
              hasChildren={!!hasChildren}
              onClick={() => handleRowExpansion(rowId, !!hasChildren)}
            />

            <NodeIcon nodeType={nodeType} />

            <span className={getRowStyle(nodeType, depth)}>{value}</span>
          </div>
        );
      },
    },
    {
      id: 'recordsVerification',
      header: () => (
        <span>
          Records Verification{' '}
          <span className="icon-saudi_riyal text-xs">&#xea;</span>
        </span>
      ),
      columns: [
        {
          id: 'recorded',
          header: 'Recorded',
          accessorKey: 'RecordsVerification.Recorded',
          cell: ({ getValue }) => {
            const value = getValue() as number;
            return (
              <span
                className={`text-right block ${getCellStyle(value, 'recorded')}`}
              >
                <DecimalNumber num={value} />
              </span>
            );
          },
        },
        {
          id: 'verified',
          header: 'Verified',
          accessorKey: 'RecordsVerification.Verified',
          cell: ({ getValue }) => {
            const value = getValue() as number;
            return (
              <span
                className={`text-right block ${getCellStyle(value, 'verified')}`}
              >
                <DecimalNumber num={value} />
              </span>
            );
          },
        },
        {
          id: 'currentDayVariances',
          header: 'Current Day Variances',
          columns: [
            {
              id: 'outstanding',
              header: 'Outstanding',
              accessorKey:
                'RecordsVerification.CurrentDayVariances.Outstanding',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'outstanding'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
            {
              id: 'exceptions',
              header: 'Exceptions',
              accessorKey:
                'RecordsVerification.CurrentDayVariances.Exceptions',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'exceptions'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
          ],
        },
        {
          id: 'cumulativeVariances',
          header: () => (
            <CumulativeVariancesHeader
              tooltipText={getCumulativeTooltipText(
                calculatedCumilativeFromDate,
                businessDay,
                cumulativeFromDate
              )}
            />
          ),
          columns: [
            {
              id: 'cumOutstanding',
              header: 'Outstanding',
              accessorKey:
                'RecordsVerification.CumulativeVariances.Outstanding',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'outstanding'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
            {
              id: 'cumExceptions',
              header: 'Exceptions',
              accessorKey:
                'RecordsVerification.CumulativeVariances.Exceptions',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'exceptions'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
          ],
        },
      ],
    },
    {
      id: 'settlementVerification',
      header: () => (
        <span>
          Settlement Verification{' '}
          <span className="icon-saudi_riyal text-xs">&#xea;</span>
        </span>
      ),
      columns: [
        {
          id: 'claimed',
          header: 'Claimed',
          accessorKey: 'SettlementVerification.Claimed',
          cell: ({ getValue }) => {
            const value = getValue() as number;
            return (
              <span
                className={`text-right block ${getCellStyle(value, 'claimed')}`}
              >
                <DecimalNumber num={value} />
              </span>
            );
          },
        },
        {
          id: 'settled',
          header: 'Settled',
          accessorKey: 'SettlementVerification.Settled',
          cell: ({ getValue }) => {
            const value = getValue() as number;
            return (
              <span
                className={`text-right block ${getCellStyle(value, 'settled')}`}
              >
                <DecimalNumber num={value} />
              </span>
            );
          },
        },
        {
          id: 'settlementCurrentDayVariances',
          header: 'Current Day Variances',
          columns: [
            {
              id: 'awaitingSettlement',
              header: 'Awaiting Settlement',
              accessorKey:
                'SettlementVerification.CurrentDayVariances.AwaitingSettlement',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'awaitingSettlement'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
            {
              id: 'settlementExceptions',
              header: 'Exceptions',
              accessorKey:
                'SettlementVerification.CurrentDayVariances.Exceptions',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'exceptions'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
          ],
        },
        {
          id: 'settlementCumulativeVariances',
          header: () => (
            <CumulativeVariancesHeader
              tooltipText={getCumulativeTooltipText(
                calculatedCumilativeFromDate,
                businessDay,
                cumulativeFromDate
              )}
            />
          ),
          columns: [
            {
              id: 'settlementCumAwaiting',
              header: 'Awaiting Settlement',
              accessorKey:
                'SettlementVerification.CumulativeVariances.AwaitingSettlement',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'awaitingSettlement'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
            {
              id: 'settlementCumExceptions',
              header: 'Exceptions',
              accessorKey:
                'SettlementVerification.CumulativeVariances.Exceptions',
              cell: ({ getValue }) => {
                const value = getValue() as number;
                return (
                  <span
                    className={`text-right block ${getCellStyle(
                      value,
                      'exceptions'
                    )}`}
                  >
                    <DecimalNumber num={value} />
                  </span>
                );
              },
            },
          ],
        },
      ],
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                viewTransaction(row.original);
              }}
              className="text-xs text-primary dark:text-white px-3 py-1 cursor-pointer"
            >
              View
            </Button>
          </div>
        );
      },
    },
  ];
}
